import { useCallback, useEffect, useRef, useState } from "react";

type Listener = () => void;
const cache = new Map<string, unknown>();
const listeners = new Map<string, Set<Listener>>();

export function invalidate(prefix: string) {
  for (const key of [...listeners.keys()]) {
    if (key === prefix || key.startsWith(prefix)) listeners.get(key)?.forEach((l) => l());
  }
}

export function useQuery<T>(key: string | null, fetcher: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | undefined>(() => (key ? (cache.get(key) as T | undefined) : undefined));
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(!!key && !cache.has(key));
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const seq = useRef(0);

  const run = useCallback(async () => {
    if (!key) return;
    const mine = ++seq.current;
    if (!cache.has(key)) setLoading(true);
    try {
      const result = await fetcherRef.current();
      if (mine !== seq.current) return;
      cache.set(key, result);
      setData(result);
      setError(null);
    } catch (e) {
      if (mine !== seq.current) return;
      setError(e as Error);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ...deps]);

  useEffect(() => {
    if (!key) return;
    setData(cache.get(key) as T | undefined);
    void run();
    const set = listeners.get(key) ?? new Set();
    set.add(run);
    listeners.set(key, set);
    return () => {
      set.delete(run);
    };
  }, [key, run]);

  return { data, error, loading, refetch: run, setData };
}
